import { useAuth } from "react-oidc-context";
import { useActiveOrg, NoAccessScreen, canUseApp } from "common-fe";
import { Box, CircularProgress } from "@mui/material";
import { colors } from "brand";
import { handleLogout } from "../utils/logout";

// AC#4 (#641) -- the app requires login (ProtectedRoute, App.jsx) AND rejects a
// user without score_announcer (or an org-scoped score role) in the active
// org. The rule itself (a role carrying list_shows_org, qualified or bare, off
// the access token's `roles` claim) lives in common-fe's canUseApp (#3656), the
// ONE copy the avatar-menu app switcher shares so it never offers an app this
// gate then refuses. The API independently enforces it; this is the FE
// affordance.

export default function AnnouncerRoleGate({ children }) {
  const auth = useAuth();
  const { activeOrgId, isSuper, orgResolved } = useActiveOrg();
  const accessToken = auth.user?.access_token;

  // #2120 (approved by Jaybill 2026-08-30, card comment): decide off
  // `orgResolved`, NOT the org-list network fetch (`loading`). For a
  // returning user, activeOrgId is already known synchronously from
  // localStorage (common-fe ActiveOrgContext), so this gate can check the
  // token's OWN roles claim for that org immediately, no waiting on the
  // GET /org round trip. Accepted trade: if the active org's role was
  // revoked since the token was minted, this can let a now-unauthorized
  // user in until they next get a fresh token — deliberately not mitigated
  // (see ActiveOrgContext.jsx). Only a brand-new session with no stored org
  // (activeOrgId still null) waits here, because there is genuinely no org
  // to check claims against yet.
  if (!orgResolved) {
    // #714 — primary.main is dark grey and invisible on a dark background;
    // force the brand accent so this gate's loading state stays visible.
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: colors.foxOrange }} />
      </Box>
    );
  }

  if (canUseApp("announcer", { accessToken, orgId: activeOrgId, isSuper })) {
    return children;
  }

  // #881 / #3338 — this gate sits OUTSIDE AnnouncerShell, so a user whose
  // active org lacks announcer access never reaches its chrome.
  // NoAccessScreen (common-fe, shared by every gate) carries the org switcher
  // + sign-out via GateHeader, names the app, says the org admin grants
  // access, and links back to the Account Center apps page.
  return (
    <NoAccessScreen
      appName="Announcer"
      onLogout={handleLogout}
      accountFeBaseUrl={import.meta.env.VITE_ACCOUNT_FE_BASE_URL}
      env={import.meta.env}
    />
  );
}
